import { FC, useEffect, useState } from 'react'
import { PageWrapper, Wrapper } from './styled'

const VISIBLE_PAGES_COUNT = 3

type Props = {
  itemsCount: number
  perPage?: number
  page?: number
  onChangePagination?: (offset: number) => void
  scrollToElementId?: string
}

const Pagination: FC<Props> = ({
  itemsCount,
  perPage = 5,
  page,
  onChangePagination,
  scrollToElementId,
}) => {
  const pages: number[] = []
  for (let i = 1; i <= Math.ceil(itemsCount / perPage); i++) {
    pages.push(i)
  }

  const [selected, setSelected] = useState(1)
  const isControlled = page !== undefined
  const totalPages = pages.at(-1) || 1

  useEffect(() => {
    if (isControlled) {
      const normalizedPage = Math.max(1, Math.min(page ?? 1, totalPages))
      setSelected((prev) => (prev === normalizedPage ? prev : normalizedPage))
      return
    }

    if (selected > totalPages) {
      setSelected(totalPages)
    }
  }, [isControlled, page, selected, totalPages])

  const scrollToElement = () => {
    if (scrollToElementId) {
      document
        .querySelector(`#${scrollToElementId}`)
        ?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleChangePage = (newPage: number) => {
    const normalizedPage = Math.max(1, Math.min(newPage, totalPages))
    setSelected(normalizedPage)
    scrollToElement()
    onChangePagination?.(normalizedPage)
  }

  const handleClickBack = () => {
    handleChangePage(selected - 1)
  }

  const handleClickNext = () => {
    handleChangePage(selected + 1)
  }

  const getPagesToRender = () => {
    if (totalPages <= VISIBLE_PAGES_COUNT) {
      return pages
    }

    const maxStartPage = totalPages - VISIBLE_PAGES_COUNT + 1
    const startPage = Math.min(Math.max(1, selected - 1), maxStartPage)

    return pages.slice(startPage - 1, startPage - 1 + VISIBLE_PAGES_COUNT)
  }

  const shouldRenderLeading = totalPages > VISIBLE_PAGES_COUNT + 1 && selected >= 4
  const pagesToRender = getPagesToRender()
  const shouldRenderTail = !pagesToRender.includes(totalPages)

  if (pagesToRender.length === 1) return null

  return (
    <Wrapper>
      {selected > 1 && (
        <PageWrapper
          type="button"
          $isWide
          $selected={false}
          onClick={handleClickBack}
        >
          ← НАЗАД
        </PageWrapper>
      )}
      {shouldRenderLeading && (
        <>
          <PageWrapper
            type="button"
            onClick={() => handleChangePage(1)}
            $selected={selected === 1}
          >
            1
          </PageWrapper>
          <PageWrapper
            type="button"
            $selected={false}
            onClick={() => handleChangePage(1)}
          >
            ...
          </PageWrapper>
        </>
      )}
      {pagesToRender.map((item) => (
        <PageWrapper
          type="button"
          onClick={() => handleChangePage(item)}
          $selected={selected === item}
          key={item}
        >
          {item}
        </PageWrapper>
      ))}
      {shouldRenderTail && (
        <>
          <PageWrapper
            type="button"
            $selected={false}
            $isStatic
            disabled
          >
            ...
          </PageWrapper>
          <PageWrapper
            type="button"
            onClick={() => handleChangePage(totalPages)}
            $selected={false}
          >
            {pages.length}
          </PageWrapper>
        </>
      )}
      {selected < totalPages && (
        <PageWrapper
          type="button"
          $isWide
          $selected={false}
          onClick={handleClickNext}
        >
          ВПЕРЕД →
        </PageWrapper>
      )}
    </Wrapper>
  )
}

export default Pagination
